import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useInView } from 'framer-motion';
import { fetchImageBlob } from '@/services/image-blob';
import { ExternalFetchError } from '@/utils/http';

const MAX_FETCH_ATTEMPTS = 6;
const LAZY_LOAD_MARGIN = '400px';
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 30000;
const RETRYABLE_STATUSES = new Set([429, 502, 503, 504]);

interface FailedResponse {
    status: number;
    retryAfterSeconds: number;
}

function failedResponse(error: unknown): FailedResponse | null {
    if (axios.isAxiosError(error) && error.response) {
        return {
            status: error.response.status,
            retryAfterSeconds: Number(error.response.headers['retry-after']),
        };
    }
    if (error instanceof ExternalFetchError) {
        return {
            status: error.response.status,
            retryAfterSeconds: Number(
                error.response.headers.get('Retry-After'),
            ),
        };
    }
    return null;
}

function retryable(error: unknown): boolean {
    const failed = failedResponse(error);
    return failed === null || RETRYABLE_STATUSES.has(failed.status);
}

function retryDelayMs(error: unknown, attempt: number): number {
    const retryAfterSeconds = failedResponse(error)?.retryAfterSeconds ?? NaN;
    const delayMs =
        retryAfterSeconds > 0
            ? retryAfterSeconds * 1000
            : RETRY_BASE_DELAY_MS * 2 ** attempt;
    return Math.min(delayMs, RETRY_MAX_DELAY_MS);
}

interface ImageProps extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'onError'
> {
    src: string;
    alt: string;
    auth?: boolean;
    onLoad?: () => void;
    onError?: () => void;
    generatingPreview?: boolean;
}

export function Image({
    src,
    alt,
    auth = false,
    className,
    onLoad,
    onError,
    generatingPreview = false,
    ...props
}: ImageProps) {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(auth);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [container, setContainer] = useState<Element | null>(null);
    const lastSrcRef = useRef(src);
    const settledSrcRef = useRef<string | null>(null);
    const objectUrlRef = useRef<string | null>(null);
    const containerRef = useMemo(() => ({ current: container }), [container]);
    const inView = useInView(containerRef, { margin: LAZY_LOAD_MARGIN });

    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
            settledSrcRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!auth) {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
            setError(false);
            setLoading(false);
            setBlobUrl(src);
            onLoad?.();
            return;
        }

        if (lastSrcRef.current !== src) {
            lastSrcRef.current = src;
            settledSrcRef.current = null;
            if (attempt !== 0) {
                setAttempt(0);
                return;
            }
        }

        if (settledSrcRef.current === src) {
            return;
        }

        if (!inView) {
            return;
        }

        let cancelled = false;
        let retryTimer: NodeJS.Timeout | null = null;

        function failOrRetry(error: unknown) {
            if (cancelled) {
                return;
            }
            if (retryable(error) && attempt < MAX_FETCH_ATTEMPTS) {
                retryTimer = setTimeout(
                    () => setAttempt((previous) => previous + 1),
                    retryDelayMs(error, attempt),
                );
                return;
            }
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
            settledSrcRef.current = src;
            setBlobUrl(null);
            setError(true);
            setLoading(false);
            onError?.();
        }

        async function fetchImage() {
            setLoading(true);
            setError(false);

            try {
                const blob = await fetchImageBlob(src);
                const objectUrl = URL.createObjectURL(blob);

                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }

                if (objectUrlRef.current) {
                    URL.revokeObjectURL(objectUrlRef.current);
                }
                objectUrlRef.current = objectUrl;
                settledSrcRef.current = src;
                setBlobUrl(objectUrl);
                setLoading(false);
                onLoad?.();
            } catch (error) {
                failOrRetry(error);
            }
        }

        fetchImage();

        return () => {
            cancelled = true;
            if (retryTimer) {
                clearTimeout(retryTimer);
            }
        };
    }, [src, auth, attempt, inView]);

    if (loading || error || !blobUrl) {
        if (generatingPreview) {
            return (
                <div
                    ref={setContainer}
                    className={`bg-grey-700 flex items-center justify-center ${className || ''}`}
                    {...props}
                >
                    <span className="text-grey-300 text-sm">
                        Generating Preview
                    </span>
                </div>
            );
        }
        if (loading) {
            return (
                <div
                    ref={setContainer}
                    className={`bg-grey-700 animate-pulse ${className || ''}`}
                    {...props}
                />
            );
        }
        return (
            <div
                ref={setContainer}
                className={`bg-grey-700 flex items-center justify-center ${className || ''}`}
                {...props}
            >
                <span className="text-grey-400 text-xs">Failed to load</span>
            </div>
        );
    }

    return (
        <img
            ref={setContainer}
            src={blobUrl}
            alt={alt}
            className={className}
            onError={() => {
                setError(true);
                onError?.();
            }}
            {...props}
        />
    );
}
